import { forwardRef, useEffect, useImperativeHandle, useRef } from 'react';
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { VRMHumanBoneName, VRMLoaderPlugin, VRMUtils, type VRM } from '@pixiv/three-vrm';
import {
  VRMAnimationLoaderPlugin,
  createVRMAnimationClip,
  type VRMAnimation,
} from '@pixiv/three-vrm-animation';
import { PLAY_BTN_MIN_DURATION_SEC } from './types';
import type { VrmaBackendHandle, VrmaBackendProps } from './types';

function createLoader(): GLTFLoader {
  const loader = new GLTFLoader();
  loader.register((parser) => new VRMLoaderPlugin(parser));
  loader.register((parser) => new VRMAnimationLoaderPlugin(parser));
  return loader;
}

/**
 * three.js によるライト版描画バックエンド。VrmaPreview（親）から VRM/VRMA の URL・再生状態を
 * 受け取り、Three.js のシーンを構築・更新するだけに専念する（UI・React state は持たない）。
 */
const VrmaViewerThree = forwardRef<VrmaBackendHandle, VrmaBackendProps>(function VrmaViewerThree(
  props,
  ref,
) {
  const { vrmUrl, vrmaSource, armed } = props;

  const hostRef = useRef<HTMLDivElement>(null);

  // Three.js のオブジェクトは再レンダーをまたいで保持したいので ref に置く。
  const sceneRef = useRef<THREE.Scene>();
  const cameraRef = useRef<THREE.PerspectiveCamera>();
  const controlsRef = useRef<OrbitControls>();
  const vrmRef = useRef<VRM>();
  const mixerRef = useRef<THREE.AnimationMixer>();
  const actionRef = useRef<THREE.AnimationAction>();
  // シークバー用: クリップの長さ（render ループとシーク処理で共有する）。
  const clipDurationRef = useRef(0);

  // props はコールバックを含み、render ループや非同期関数（loadVrm/loadVrma）から常に
  // 最新版を参照する必要があるため ref にミラーする（Three 初期化 effect は [] 依存なので、
  // 素の props をクロージャで掴むと古いコールバックのままになってしまう）。
  const propsRef = useRef(props);
  propsRef.current = props;

  // render ループから読む値は ref ミラーにする（毎レンダーで直接代入するだけで十分で、
  // 副作用を伴わないためエフェクトは不要）。
  const seekingRef = useRef(props.scrubbing);
  seekingRef.current = props.scrubbing;
  const renderingRef = useRef(props.rendering);
  renderingRef.current = props.rendering;

  // playing を render ループから参照するための ref ミラー。
  const playingRef = useRef(props.playing);
  useEffect(() => {
    playingRef.current = props.playing;
    if (actionRef.current) actionRef.current.paused = !props.playing;
  }, [props.playing]);

  // ---- Three.js 初期化（マウント時に一度だけ）----
  useEffect(() => {
    const host = hostRef.current!;
    const width = host.clientWidth;
    const height = host.clientHeight;

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x11181a);
    sceneRef.current = scene;

    const camera = new THREE.PerspectiveCamera(35, width / height, 0.1, 100);
    camera.position.set(0, 1.3, 2.4);
    cameraRef.current = camera;

    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(width, height);
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    host.appendChild(renderer.domElement);

    const controls = new OrbitControls(camera, renderer.domElement);
    controls.target.set(0, 1.0, 0);
    controls.enableDamping = true;
    controls.update();
    controlsRef.current = controls;

    // ライティング
    const hemi = new THREE.HemisphereLight(0xffffff, 0x444455, 1.2);
    scene.add(hemi);
    const dir = new THREE.DirectionalLight(0xffffff, 1.4);
    dir.position.set(1, 2, 1.5);
    scene.add(dir);

    // 床グリッド
    const grid = new THREE.GridHelper(10, 20, 0x335555, 0x223333);
    scene.add(grid);

    const clock = new THREE.Clock();
    let raf = 0;
    // シークバーの経過時間表示は毎フレーム onProgress すると負荷が高いので、約0.1秒間隔に間引く。
    let elapsedAccum = 0;
    // 描画停止中に溜まった経過時間を復帰時に食ってアニメーションが飛ぶのを防ぐためのフラグ。
    let clockStale = false;
    const animate = () => {
      raf = requestAnimationFrame(animate);
      if (!renderingRef.current) {
        clockStale = true;
        return;
      }
      if (clockStale) {
        // 停止中に溜まった分を1回読み捨てる。
        clock.getDelta();
        clockStale = false;
      }
      const delta = clock.getDelta();
      // ドラッグ中（seeking）は再生中でも進行を止める。指を離せば playingRef の状態に従って
      // 自然に再開/停止するため、元が一時停止なら離してもそのまま止まったままになる。
      if (mixerRef.current && playingRef.current && !seekingRef.current)
        mixerRef.current.update(delta);
      vrmRef.current?.update(delta);
      if (actionRef.current && clipDurationRef.current > 0 && !seekingRef.current) {
        elapsedAccum += delta;
        if (elapsedAccum >= 0.1) {
          elapsedAccum = 0;
          propsRef.current.onProgress(actionRef.current.time);
        }
      }
      controls.update();
      renderer.render(scene, camera);
    };
    animate();

    // リサイズ対応
    const ro = new ResizeObserver(() => {
      const w = host.clientWidth;
      const h = host.clientHeight;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
    });
    ro.observe(host);

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      controls.dispose();
      renderer.dispose();
      // WebGL context の上限（ブラウザによりおよそ16）に達しないよう、破棄と同時に明示的に
      // context を手放す。繰り返しマウント・アンマウントされるようになった際の対策。
      renderer.forceContextLoss();
      renderer.domElement.remove();
      if (vrmRef.current) VRMUtils.deepDispose(vrmRef.current.scene);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Hips の位置を基準に、全身がフレームに収まるようカメラと controls を再配置する。
  function frameCameraOnVrm(vrm: VRM) {
    const camera = cameraRef.current;
    const controls = controlsRef.current;
    if (!camera || !controls) return;

    vrm.scene.updateMatrixWorld(true);

    const hipsNode = vrm.humanoid?.getNormalizedBoneNode(VRMHumanBoneName.Hips);
    if (!hipsNode) return;
    const hipPos = hipsNode.getWorldPosition(new THREE.Vector3());

    const headNode = vrm.humanoid?.getNormalizedBoneNode(VRMHumanBoneName.Head);
    const headPos = headNode?.getWorldPosition(new THREE.Vector3());

    const hipY = hipPos.y;
    const headTopY = headPos ? headPos.y + 0.15 : hipY + 0.9;
    const halfHeight = Math.max(hipY, headTopY - hipY) * 1.1;
    const fovRad = THREE.MathUtils.degToRad(camera.fov);
    const distance = THREE.MathUtils.clamp(halfHeight / Math.tan(fovRad / 2), 1.5, 5);

    controls.target.set(0, hipY, 0);
    camera.position.set(0, hipY, distance);
    controls.update();
  }

  // ---- VRM 読み込み ----
  async function loadVrm(source: string | File) {
    propsRef.current.onStatusChange('loading');
    const isFile = typeof source !== 'string';
    try {
      const url = isFile ? URL.createObjectURL(source) : source;
      const loader = createLoader();
      let gltf;
      try {
        gltf = await loader.loadAsync(url);
      } finally {
        // File から作った objectURL は使い終わったら解放する（文字列 URL は呼び出し側の
        // 所有物 — protectedAsset.ts がキャッシュしている blob URL 等 — なので revoke しない）。
        if (isFile) URL.revokeObjectURL(url);
      }
      const vrm = gltf.userData.vrm as VRM;

      // 不要なジョイントを除去して負荷軽減。VRM0 系は後ろ向きなので回転。
      VRMUtils.removeUnnecessaryVertices(gltf.scene);
      VRMUtils.combineSkeletons(gltf.scene);
      VRMUtils.rotateVRM0(vrm);

      // 既存 VRM を入れ替え
      if (vrmRef.current) {
        sceneRef.current?.remove(vrmRef.current.scene);
        VRMUtils.deepDispose(vrmRef.current.scene);
        mixerRef.current = undefined;
        actionRef.current = undefined;
        clipDurationRef.current = 0;
      }
      vrmRef.current = vrm;
      sceneRef.current?.add(vrm.scene);
      mixerRef.current = new THREE.AnimationMixer(vrm.scene);
      frameCameraOnVrm(vrm);
      propsRef.current.onLoaded({ hasVrm: true, hasClip: false, durationSec: 0 });
      propsRef.current.onStatusChange('ready');

      // 親から渡り続けている vrmaSource があれば、VRM に続けて適用する。
      if (propsRef.current.vrmaSource) {
        await loadVrma(propsRef.current.vrmaSource);
      }
    } catch (e) {
      console.error(e);
      propsRef.current.onError('VRM の読み込みに失敗しました。別のファイルを選択してください。');
      propsRef.current.onStatusChange('error');
    }
  }

  // ---- VRMA 読み込み・適用 ----
  // loadVrm の末尾からのみ呼ばれる（VRM 読み込み完了後）ため、vrmRef/mixerRef が
  // 未設定のケースは考慮しない。
  async function loadVrma(source: string | File) {
    propsRef.current.onStatusChange('loading');
    const isFile = typeof source !== 'string';
    try {
      const url = isFile ? URL.createObjectURL(source) : source;
      const loader = createLoader();
      let gltf;
      try {
        gltf = await loader.loadAsync(url);
      } finally {
        if (isFile) URL.revokeObjectURL(url);
      }
      const vrmAnimations = gltf.userData.vrmAnimations as VRMAnimation[] | undefined;
      if (!vrmAnimations || vrmAnimations.length === 0) {
        throw new Error('VRMAnimation が含まれていません');
      }
      const clip = createVRMAnimationClip(vrmAnimations[0], vrmRef.current!);

      actionRef.current?.stop();
      mixerRef.current!.stopAllAction();
      const action = mixerRef.current!.clipAction(clip);
      action.play();
      // 再生/一時停止ボタンを出さない短尺クリップは、直前の一時停止状態を引き継ぐと
      // 再開手段が無くなるため、必ず再生状態に戻す（state は親の onLoaded 側で追従させる）。
      if (clip.duration <= PLAY_BTN_MIN_DURATION_SEC) {
        playingRef.current = true;
      }
      action.paused = !playingRef.current;
      actionRef.current = action;
      // シークバー用にクリップの長さを控える。
      clipDurationRef.current = clip.duration;
      propsRef.current.onLoaded({ hasVrm: true, hasClip: true, durationSec: clip.duration });
      propsRef.current.onStatusChange('ready');
    } catch (e) {
      console.error(e);
      propsRef.current.onError('VRMA の読み込みに失敗しました。.vrma ファイルかご確認ください。');
      propsRef.current.onStatusChange('error');
    }
  }

  // armed が立ってから、vrmUrl / vrmaSource の変化に応じて読み込む。
  // 読み込んだ VRM に続けて適用する VRMA は loadVrm の末尾で propsRef.current.vrmaSource を
  // 見て決めるため、ここでは armed かどうかと VRM/VRMA の変化だけを見ればよい。
  //
  // VRMA だけが変わった場合も VRM を読み直している（分割前の挙動を踏襲）。一見無駄だが、
  // VRMA が一部のボーンしか含まない場合に前のクリップのポーズが残ってしまうため、
  // 素体から作り直すのが確実。ロード時間との引き換えだが正しさを優先する。
  useEffect(() => {
    if (!armed) return;
    void loadVrm(vrmUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [armed, vrmUrl, vrmaSource]);

  useImperativeHandle(
    ref,
    () => ({
      // シークバーのドラッグ・クリックで再生位置を変更する。一時停止中でもポーズを即座に反映させる。
      seek(seconds: number) {
        const clamped = Math.min(Math.max(seconds, 0), clipDurationRef.current);
        if (actionRef.current) actionRef.current.time = clamped;
        mixerRef.current?.update(0);
        vrmRef.current?.update(0);
      },
      resetCamera() {
        if (vrmRef.current) frameCameraOnVrm(vrmRef.current);
      },
      getElapsed() {
        return actionRef.current?.time ?? 0;
      },
    }),
    [],
  );

  return <div className="viewer__layer" ref={hostRef} />;
});

export default VrmaViewerThree;
